import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { X, Sparkle, ListChecks, Notebook, Plus, Spinner } from "@phosphor-icons/react";
import { CategoryBadge, PriorityBadge } from "@/components/Badges";

export default function AIAssistant({ open, onClose, onTasksCreated }) {
  const { user } = useAuth();
  const isManager = ["owner", "store_manager", "assistant_manager"].includes(user.role);
  const [tab, setTab] = useState("summary");
  const [summary, setSummary] = useState("");
  const [loadingSum, setLoadingSum] = useState(false);
  const [notes, setNotes] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [creating, setCreating] = useState(false);

  if (!open) return null;

  const runSummary = async () => {
    setLoadingSum(true);
    try { const { data } = await api.post("/ai/summarize"); setSummary(data.summary); }
    catch { toast.error("AI failed"); } finally { setLoadingSum(false); }
  };

  const extract = async () => {
    if (!notes.trim()) { toast.error("Paste some notes first"); return; }
    setLoadingDrafts(true);
    try { const { data } = await api.post("/ai/create-tasks", { notes }); setDrafts(data.tasks || []); if (!data.tasks?.length) toast.info("No tasks found"); }
    catch { toast.error("AI failed"); } finally { setLoadingDrafts(false); }
  };

  const createAll = async () => {
    setCreating(true);
    try {
      for (const d of drafts) await api.post("/tasks", { ...d, status: "pending" });
      toast.success(`Created ${drafts.length} tasks`);
      setDrafts([]); setNotes(""); onTasksCreated();
    } catch { toast.error("Failed to create"); } finally { setCreating(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div data-testid="ai-assistant-panel" onClick={(e) => e.stopPropagation()} className="relative bg-white border-l-2 border-black w-full max-w-md h-full overflow-y-auto">
        <div className="sticky top-0 bg-[#F3E8FF] border-b-2 border-black px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-secondary border-2 border-black flex items-center justify-center"><Sparkle size={18} weight="fill" color="#fff" /></div>
            <h2 className="font-display text-xl font-bold">AI Assistant</h2>
          </div>
          <button data-testid="close-ai-btn" onClick={onClose} className="press w-9 h-9 rounded-full border-2 border-black bg-white flex items-center justify-center"><X size={18} weight="bold" /></button>
        </div>

        <div className="p-5">
          <div className="flex gap-2 mb-5">
            <button data-testid="ai-tab-summary" onClick={() => setTab("summary")} className={`press flex-1 h-10 rounded-full text-sm font-bold border-2 border-black flex items-center justify-center gap-1 ${tab === "summary" ? "bg-primary text-white" : "bg-white"}`}><ListChecks size={16} weight="bold" /> Summary</button>
            {isManager && <button data-testid="ai-tab-create" onClick={() => setTab("create")} className={`press flex-1 h-10 rounded-full text-sm font-bold border-2 border-black flex items-center justify-center gap-1 ${tab === "create" ? "bg-primary text-white" : "bg-white"}`}><Notebook size={16} weight="bold" /> Notes → Tasks</button>}
          </div>

          {tab === "summary" && (
            <div>
              <p className="text-sm text-black/60 mb-3">Get an AI summary of everything unfinished so you know what needs attention.</p>
              <button data-testid="run-summary-btn" onClick={runSummary} disabled={loadingSum} className="press w-full h-11 rounded-full bg-secondary text-white font-bold border-2 border-black flex items-center justify-center gap-2">
                {loadingSum ? <><Spinner size={16} className="animate-spin" /> Thinking…</> : <><Sparkle size={16} weight="fill" /> Summarize Unfinished</>}
              </button>
              {summary && <div data-testid="ai-summary-output" className="mt-4 bg-[#F9F9F6] border-2 border-black rounded-lg p-4 text-sm whitespace-pre-wrap">{summary}</div>}
            </div>
          )}

          {tab === "create" && isManager && (
            <div>
              <p className="text-sm text-black/60 mb-3">Paste messy notes. AI extracts clean, categorized tasks.</p>
              <textarea data-testid="ai-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full min-h-[120px] p-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm" placeholder="call supplier about candles, front window needs new display, pay electric bill friday…" />
              <button data-testid="ai-extract-btn" onClick={extract} disabled={loadingDrafts} className="press w-full h-11 mt-3 rounded-full bg-secondary text-white font-bold border-2 border-black flex items-center justify-center gap-2">
                {loadingDrafts ? <><Spinner size={16} className="animate-spin" /> Extracting…</> : <><Sparkle size={16} weight="fill" /> Extract Tasks</>}
              </button>

              {drafts.length > 0 && (
                <div className="mt-4 space-y-2">
                  {drafts.map((d, i) => (
                    <div key={i} data-testid={`draft-${i}`} className="border-2 border-black rounded-lg p-3 bg-white">
                      <p className="font-bold text-sm">{d.title}</p>
                      {d.notes && <p className="text-xs text-black/60 mt-1">{d.notes}</p>}
                      <div className="flex gap-2 mt-2"><CategoryBadge category={d.category} /><PriorityBadge priority={d.priority} /></div>
                    </div>
                  ))}
                  <button data-testid="create-all-btn" onClick={createAll} disabled={creating} className="press w-full h-11 rounded-full bg-primary text-white font-bold border-2 border-black flex items-center justify-center gap-2"><Plus size={16} weight="bold" /> {creating ? "Creating…" : `Create ${drafts.length} Tasks`}</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
